import type { HotkeyHandler, KeysMap } from '@hooks/useHotKeys';
import type { ContextPool } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import {
  handle2DLabel,
  handleDelete,
  handleEdit3DShortcut,
  handleEditBrushSizeShortcut,
  handleEditCoron,
  handleEditEraser3DShortcut,
  handleEditToolShortcut,
  handleEscape,
  handleKeyEnter,
  handlePointConnect,
  handlePrimaryNavigationToolShortcut,
  handleRedo,
  handleToolShortcut,
  handleUndo,
} from 'utils/hotKeyActions';
import type { AnalysisType } from './analysisConfig';
import type { PanelComponentKeyType } from './panelConfig';
import type { ToolId } from './toolDefinitionsConfig';

export type AnalysisHotkeyCtx = {
  analysisType: AnalysisType;
  context: ContextPool;
  selectedPanelComponentKey: PanelComponentKeyType;
  activeToolId?: ToolId;
  handleToolActivate?: (toolId: ToolId) => void;
};

type AnalysisHotkeyRuntimeCtx = AnalysisHotkeyCtx & {
  event: KeyboardEvent;
  key: string;
};

const withAnalysisCtx =
  (
    handler: (
      ctx: AnalysisHotkeyRuntimeCtx,
    ) => ReturnType<HotkeyHandler<AnalysisHotkeyCtx>>,
  ): HotkeyHandler<AnalysisHotkeyCtx> =>
  (ctx) => {
    if (!ctx) return false;
    return handler(ctx);
  };

const withNavigationToolShortcut = ({
  defaultToolId,
  chamberToolId,
  defaultSelectedPanelRequired = false,
  chamberSelectedPanelRequired = false,
}: {
  defaultToolId?: ToolId;
  chamberToolId: ToolId;
  defaultSelectedPanelRequired?: boolean;
  chamberSelectedPanelRequired?: boolean;
}) =>
  withAnalysisCtx(
    ({ analysisType, selectedPanelComponentKey, handleToolActivate }) => {
      if (analysisType === 'chamber') {
        return handleToolShortcut({
          toolId: chamberToolId,
          handleToolActivate,
          ...(chamberSelectedPanelRequired
            ? { selectedPanelComponentKey }
            : {}),
        });
      }

      if (!defaultToolId) return false;

      return handleToolShortcut({
        toolId: defaultToolId,
        handleToolActivate,
        ...(defaultSelectedPanelRequired ? { selectedPanelComponentKey } : {}),
      });
    },
  );

export const hotkeysConfigAnalysis: KeysMap<AnalysisHotkeyCtx> = {
  Escape: withAnalysisCtx(({ selectedPanelComponentKey }) =>
    handleEscape(selectedPanelComponentKey),
  ),
  Delete: () => handleDelete(),
  z: withAnalysisCtx(({ selectedPanelComponentKey }) =>
    handleUndo(selectedPanelComponentKey),
  ),
  x: withAnalysisCtx(({ selectedPanelComponentKey }) =>
    handleRedo(selectedPanelComponentKey),
  ),
  'Control+Shift+Z': withAnalysisCtx(({ selectedPanelComponentKey }) =>
    handleRedo(selectedPanelComponentKey),
  ),

  'Control+`': withAnalysisCtx(({ handleToolActivate }) =>
    handleToolShortcut({
      toolId: 'ResetView',
      handleToolActivate,
    }),
  ),
  '1': withAnalysisCtx(
    ({ analysisType, selectedPanelComponentKey, handleToolActivate }) => {
      if (analysisType === 'chamber') {
        return handleToolShortcut({
          toolId: 'Scroll',
          handleToolActivate,
        });
      }

      return handlePrimaryNavigationToolShortcut({
        selectedPanelComponentKey,
        handleToolActivate,
      });
    },
  ),
  '2': withNavigationToolShortcut({
    defaultToolId: 'Pan',
    chamberToolId: 'Rotation',
  }),
  '3': withNavigationToolShortcut({
    defaultToolId: 'Zoom',
    chamberToolId: 'Pan',
  }),
  '4': withNavigationToolShortcut({
    defaultToolId: 'WindowLevel',
    chamberToolId: 'Zoom',
    defaultSelectedPanelRequired: true,
  }),
  '5': withNavigationToolShortcut({
    chamberToolId: 'WindowLevel',
    chamberSelectedPanelRequired: true,
  }),

  b: withAnalysisCtx(({ selectedPanelComponentKey, handleToolActivate }) =>
    handleEditToolShortcut('Brush',selectedPanelComponentKey, handleToolActivate),
  ),
  e: withAnalysisCtx(
    ({ selectedPanelComponentKey, handleToolActivate, activeToolId }) =>
      handleEditToolShortcut('Eraser',selectedPanelComponentKey, handleToolActivate),
  ),
  Shift: withAnalysisCtx(
    ({ selectedPanelComponentKey, handleToolActivate, activeToolId }) =>
      handleEditEraser3DShortcut('eraser', selectedPanelComponentKey),
  ),

  ArrowUp: withAnalysisCtx(
    ({ selectedPanelComponentKey, handleToolActivate, activeToolId }) =>
      handleEditBrushSizeShortcut({
        selectedPanelComponentKey,
        activeToolId,
        delta: 1,
      }),
    //handleEditBrushShortcut(selectedPanelComponentKey, handleToolActivate),
  ),
  // r: withAnalysisCtx(
  //   ({
  //     selectedPanelComponentKey,
  //     handleToolActivate,
  //     analysisType,
  //     context,
  //   }) =>
  //     handleEditEraserShortcut(selectedPanelComponentKey, handleToolActivate) ||
  //     handle2DLabel(analysisType, context),
  // ),
  ArrowDown: withAnalysisCtx(
    ({ selectedPanelComponentKey, handleToolActivate, activeToolId }) =>
      handleEditBrushSizeShortcut({
        selectedPanelComponentKey,
        activeToolId,
        delta: -1,
      }),
    //handleEditEraserShortcut(selectedPanelComponentKey, handleToolActivate),
  ),

  // q: withAnalysisCtx(({ selectedPanelComponentKey, activeToolId }) =>
  //   handleEditBrushSizeShortcut({
  //     selectedPanelComponentKey,
  //     activeToolId,
  //     delta: -1,
  //   }),
  // ),
  ArrowLeft: withAnalysisCtx(({ selectedPanelComponentKey, activeToolId }) =>
    handleEditBrushSizeShortcut({
      selectedPanelComponentKey,
      activeToolId,
      delta: -1,
    }),
  ),
  // w: withAnalysisCtx(({ selectedPanelComponentKey, activeToolId }) =>
  //   handleEditBrushSizeShortcut({
  //     selectedPanelComponentKey,
  //     activeToolId,
  //     delta: 1,
  //   }),
  // ),
  ArrowRight: withAnalysisCtx(({ selectedPanelComponentKey, activeToolId }) =>
    handleEditBrushSizeShortcut({
      selectedPanelComponentKey,
      activeToolId,
      delta: 1,
    }),
  ),
  s: (ctx) => handle2DLabel(ctx!.analysisType, ctx!.context),
  o: (ctx) => handleEditCoron(ctx!.selectedPanelComponentKey),
  //'f': (ctx) => handleKeyF(ctx!.analysisType, ctx!.selectedPanelComponentKey),
  c: (ctx) =>
    handlePointConnect(ctx!.analysisType, ctx!.selectedPanelComponentKey),
  d: (ctx) => handleEdit3DShortcut('dilate', ctx!.selectedPanelComponentKey),

  // Previous 3D direction shortcuts. Kept here in case they are needed again.
  // 'a': withAnalysisCtx(({ context, selectedPanelComponentKey }) =>
  //   handleKeyA(context, selectedPanelComponentKey),
  // ),
  // 'l': withAnalysisCtx(({ context, selectedPanelComponentKey }) =>
  //   handleKeyL(context, selectedPanelComponentKey),
  // ),
  // 'h': withAnalysisCtx(({ context, selectedPanelComponentKey }) =>
  //   handleKeyH(context, selectedPanelComponentKey),
  // ),
  // 'p': withAnalysisCtx(({ context, selectedPanelComponentKey }) =>
  //   handleKeyP(context, selectedPanelComponentKey),
  // ),
  // 'r': withAnalysisCtx(({ context, selectedPanelComponentKey }) =>
  //   handleKeyR(context, selectedPanelComponentKey),
  // ),
  // 'f': withAnalysisCtx(({ context, selectedPanelComponentKey }) =>
  //   handleKeyF(context, selectedPanelComponentKey),
  // ),
  Enter: withAnalysisCtx(({ selectedPanelComponentKey }) =>
    handleKeyEnter(selectedPanelComponentKey),
  ),

  // Label and line visibility shortcuts kept here for a future restore.
  // '1': withAnalysisCtx(({ analysisType, context }) =>
  //   handleLumenLabel(analysisType, context),
  // ),
  // '2': withAnalysisCtx(({ analysisType, context }) =>
  //   handlePlaqueLabel(analysisType, context),
  // ),
  // '3': withAnalysisCtx(({ analysisType, context }) =>
  //   handleFaiLabel(analysisType, context),
  // ),
  // '4': withAnalysisCtx(({ analysisType, context }) =>
  //   handleLumenOutline(analysisType, context),
  // ),
  // '5': withAnalysisCtx(({ analysisType, context }) =>
  //   handlePlaqueOutline(analysisType, context),
  // ),
  // '6': withAnalysisCtx(({ analysisType, context }) =>
  //   handleMarkerline(analysisType, context),
  // ),
  // '7': withAnalysisCtx(({ analysisType, context }) =>
  //   handleGuideline(analysisType, context),
  // ),
  // '8': withAnalysisCtx(({ analysisType, context }) =>
  //   handleStenosisline(analysisType, context),
  // ),
  // '9': withAnalysisCtx(({ analysisType, context }) =>
  //   handleIsrline(analysisType, context),
  // ),
};

export const hotkeysUpConfigAnalysis: KeysMap<AnalysisHotkeyCtx> = {
  Shift: withAnalysisCtx(({ selectedPanelComponentKey }) =>
    handleEditEraser3DShortcut('none', selectedPanelComponentKey),
  ),
};

export type AppHotkeyCtx = {};

export const hotkeysConfigApp: KeysMap<AppHotkeyCtx> = {
  'Alt+F4': () => {
    console.log('Alt+F4 pressed (may not be interceptable in browsers)');
  },
};
